import PortalIdParser from 'PortalIdParser';
import Raven from 'raven-js';

import isObject from 'transmute/isObject';

import { AIPropertiesProcessingMetadata } from './AIPropertiesProcessingContextProvider';
import { makeIdentifierKey, parseIdentifierKey } from './identifierKey';

// Metadata for properties that started processing over 24 hours ago is stale and will be ignored
const STALENESS_THRESHOLD = 1000 * 60 * 60 * 24;

function makePersistenceKey(): string {
  return `smartProperties_${PortalIdParser.get()}`;
}

/**
 * PersistedMetadata is of the format:
 * @example
 * {
 *   "<identifierKey>": timestamp,
 *   "<identifierKey>": timestamp,
 *   ...
 * }
 */
type PersistedMetadata = Record<string, number>;

function parseRecentTimestamp(rawTimestamp: unknown): number | null {
  if (typeof rawTimestamp !== 'number' && typeof rawTimestamp !== 'string') {
    return null;
  }
  const parsedTimestamp =
    typeof rawTimestamp !== 'number'
      ? parseInt(`${rawTimestamp}`, 10)
      : rawTimestamp;
  if (isNaN(parsedTimestamp)) {
    Raven.captureException(new Error('Invalid timestamp in local storage'), {
      extra: { rawTimestamp },
    });
    return null;
  }
  if (Date.now() - parsedTimestamp >= STALENESS_THRESHOLD) {
    return null;
  }
  return parsedTimestamp;
}

function deserializeMetadata(
  rawPersistedMetadata: unknown
): AIPropertiesProcessingMetadata {
  if (
    rawPersistedMetadata === null ||
    typeof rawPersistedMetadata !== 'string'
  ) {
    return {};
  }

  try {
    const persistedMetadata = JSON.parse(rawPersistedMetadata);
    if (persistedMetadata === null || !isObject(persistedMetadata)) {
      return {};
    }

    const parsedMetadata: AIPropertiesProcessingMetadata = {};
    for (const [identifierKey, timestamp] of Object.entries(
      persistedMetadata
    )) {
      const { objectTypeId, propertyName, objectId } =
        parseIdentifierKey(identifierKey);
      const startedAt = parseRecentTimestamp(timestamp);
      if (startedAt !== null) {
        parsedMetadata[
          makeIdentifierKey(objectTypeId, propertyName, objectId)
        ] = {
          status: 'pending',
          isLoadingNewValue: false,
          startedAt,
          metadata: {},
        };
      }
    }
    return parsedMetadata;
  } catch (error) {
    return {};
  }
}

function serializeMetadata(
  metadata: AIPropertiesProcessingMetadata
): PersistedMetadata {
  const serializedMetadataByObjectTypeId = Object.entries(
    metadata
  ).reduce<PersistedMetadata>((acc, [key, value]) => {
    if (value.status === 'pending') {
      acc[key] = value.startedAt;
    }

    return acc;
  }, {});
  return serializedMetadataByObjectTypeId;
}

export function loadProcessingMetadata(
  rawMetadata?: string | null
): AIPropertiesProcessingMetadata {
  try {
    const metadata =
      rawMetadata !== undefined
        ? rawMetadata
        : localStorage.getItem(makePersistenceKey());
    return metadata ? deserializeMetadata(metadata) : {};
  } catch (e) {
    Raven.captureException(new Error('Error retrieving metadata'), {
      extra: { e },
    });
    return {};
  }
}

export function persistProcessingMetadata(
  metadata: AIPropertiesProcessingMetadata
): void {
  try {
    const serializedMetadata = serializeMetadata(metadata);
    const persistenceKey = makePersistenceKey();
    if (Object.keys(serializedMetadata).length > 0) {
      localStorage.setItem(persistenceKey, JSON.stringify(serializedMetadata));
    } else {
      localStorage.removeItem(persistenceKey);
    }
  } catch (e) {
    Raven.captureException(new Error('Error persisting metadata'), {
      extra: { e },
    });
  }
}

export const EXPORTED_FOR_TESTING = {
  STALENESS_THRESHOLD,
  parseRecentTimestamp,
  deserializeMetadata,
  serializeMetadata,
};
